/**
 * event-qr-modal.js — the door-sales QR code (task #724).
 *
 * A host running a ticketed event needs one thing at the venue entrance: a
 * sheet of paper a walk-up can point a phone at and buy a ticket. This module
 * is that sheet. It is shared by the event cards on /my-events and by the
 * single-event dashboard, so the two can never drift apart in what they print.
 *
 * The one design decision worth stating: the modal does NOT show a bare QR
 * beside a pair of buttons. It composes the whole SIGN onto a canvas — title,
 * "Scan to buy tickets", the code, the address underneath — and shows that
 * image. Print and Download then hand over the exact pixels on screen, so
 * there is no way for the preview and the artifact to disagree. A bare QR
 * download already exists for the RSVP form and website cards elsewhere
 * (my-projects.js `_downloadQrPng`); this is deliberately a different artifact,
 * not a second copy of that one.
 *
 * Printing uses a @media print rule rather than an iframe or a popup: popups
 * get blocked and iframes print inconsistently across browsers, while hiding
 * every sibling of the backdrop prints what the host is already looking at.
 *
 * Depends on qrcode-generator (window.qrcode) and, optionally, window.eventT
 * for translation — it falls back to English when either is absent.
 */
(function () {
  'use strict';

  if (window.EventQrModal) return;

  // Sign geometry, in device pixels. 1200x1600 is 8" x 10.67" at 150dpi:
  // comfortably inside a Letter or A4 portrait page with room for margins,
  // and dense enough that the code stays crisp on an office laser printer.
  var SIGN_W = 1200;
  var SIGN_H = 1600;
  var PAD = 90;

  var FONT_STACK = '"Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';
  var MONO_STACK = '"SF Mono", Consolas, "Liberation Mono", monospace';

  function eventT(key, fallback) {
    if (typeof window.eventT === 'function') {
      var v = window.eventT(key, fallback);
      if (v) return v;
    }
    return fallback;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Styles. Injected once, scoped to eqr- so neither host page can collide
  // with it, and built on the CSS variables both pages already define.
  function injectStyles() {
    if (document.getElementById('eqr-styles')) return;
    var st = document.createElement('style');
    st.id = 'eqr-styles';
    st.textContent = [
      // No align-items:center here on purpose. A centred flex item in a
      // scrollable container has its overflowing TOP clipped and unreachable,
      // which on a 720px-tall window hid this modal's own heading. `margin:auto`
      // on the card centres it while there is room and gives up when there
      // isn't, which is the behaviour a dialog needs.
      '.eqr-backdrop{position:fixed;inset:0;background:rgba(28,28,34,.55);backdrop-filter:blur(2px);',
      'display:none;justify-content:center;z-index:1200;padding:1rem;overflow-y:auto;}',
      '.eqr-backdrop.open{display:flex;}',
      '.eqr-card{background:var(--bg-card,#fff);border-radius:14px;max-width:440px;width:100%;',
      'padding:1.4rem 1.4rem 1.2rem;box-shadow:0 24px 48px rgba(20,20,30,.25);margin:auto;}',
      '.eqr-head{display:flex;align-items:flex-start;gap:.75rem;margin-bottom:.9rem;}',
      '.eqr-head h3{font-size:1.1rem;font-weight:600;margin:0 0 .2rem;color:var(--text,#1c1c22);}',
      '.eqr-head p{margin:0;font-size:.85rem;color:var(--text-faint,#80808a);}',
      '.eqr-x{flex-shrink:0;width:32px;height:32px;border:none;background:transparent;border-radius:8px;',
      'color:var(--text-faint,#80808a);font-size:1.1rem;cursor:pointer;line-height:1;}',
      '.eqr-x:hover{background:var(--bg-elev,rgba(0,0,0,.04));color:var(--text,#1c1c22);}',
      // The sign is 3:4, so at full card width it alone is taller than a laptop
      // window. Capped, so the whole dialog — heading, draft warning and both
      // buttons — stays on screen without scrolling.
      '.eqr-preview{border:1px solid var(--border,rgba(0,0,0,.09));border-radius:10px;overflow:hidden;',
      'background:#fff;display:flex;align-items:center;justify-content:center;min-height:180px;',
      'max-height:min(52vh,560px);}',
      '.eqr-preview img{display:block;max-width:100%;max-height:min(52vh,560px);width:auto;height:auto;}',
      '.eqr-warn{display:flex;gap:.5rem;align-items:flex-start;margin-top:.9rem;padding:.6rem .7rem;',
      'border-radius:8px;background:rgba(176,132,0,.10);border:1px solid rgba(176,132,0,.35);',
      'color:#7a5c00;font-size:.82rem;line-height:1.4;}',
      '.eqr-status{margin:.7rem 0 0;font-size:.82rem;min-height:1.1em;color:var(--text-faint,#80808a);}',
      '.eqr-status.err{color:#b00020;}',
      '.eqr-actions{display:flex;flex-wrap:wrap;gap:.5rem;margin-top:1rem;}',
      // 160px basis with nowrap: side by side wherever both labels fit whole,
      // and stacked full-width below that. A two-line "Download image" beside
      // a one-line "Print QR code" was the alternative, and it read as broken.
      '.eqr-btn{flex:1 1 160px;display:inline-flex;align-items:center;justify-content:center;gap:.4rem;',
      'white-space:nowrap;',
      'padding:.55rem .9rem;border-radius:8px;font-size:.9rem;font-weight:600;cursor:pointer;',
      'font-family:inherit;border:1px solid var(--border-gold,rgba(156,122,62,.4));',
      'background:transparent;color:var(--gold-deep,#7e612f);transition:background .15s;}',
      '.eqr-btn:hover{background:rgba(156,122,62,.08);}',
      '.eqr-btn.primary{background:var(--gold,#9c7a3e);border-color:var(--gold,#9c7a3e);color:#fff;}',
      '.eqr-btn.primary:hover{background:var(--gold-deep,#7e612f);}',
      '.eqr-btn[disabled]{opacity:.55;cursor:default;}',
      // The print rule. Everything outside the backdrop is hidden, the backdrop
      // stops being an overlay, and only the sign image is left on the page.
      '@media print{',
      'body>*:not(.eqr-backdrop){display:none !important;}',
      '.eqr-backdrop{position:static !important;display:block !important;background:none !important;',
      'backdrop-filter:none !important;padding:0 !important;overflow:visible !important;}',
      '.eqr-card{max-width:none !important;box-shadow:none !important;padding:0 !important;margin:0 !important;}',
      '.eqr-noprint{display:none !important;}',
      '.eqr-preview{border:none !important;border-radius:0 !important;min-height:0 !important;',
      'max-height:none !important;}',
      '.eqr-preview img{width:100% !important;max-height:none !important;height:auto !important;}',
      '@page{margin:12mm;}',
      '}'
    ].join('');
    document.head.appendChild(st);
  }

  // Canvas text helpers.
  function wrapLines(ctx, text, maxWidth, maxLines) {
    var words = String(text || '').split(/\s+/).filter(Boolean);
    var lines = [];
    var line = '';
    for (var i = 0; i < words.length; i++) {
      var probe = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(probe).width <= maxWidth || !line) {
        line = probe;
      } else {
        lines.push(line);
        line = words[i];
      }
      if (lines.length === maxLines) break;
    }
    if (line && lines.length < maxLines) lines.push(line);
    // Ellipsize the last line if any words had to be dropped.
    var used = lines.join(' ').split(/\s+/).filter(Boolean).length;
    if (used < words.length && lines.length) {
      var last = lines[lines.length - 1];
      while (last.length > 1 && ctx.measureText(last + '…').width > maxWidth) {
        last = last.slice(0, -1);
      }
      lines[lines.length - 1] = last + '…';
    }
    return lines;
  }

  // Shrink a single line until it fits — used for the address, which must
  // never be truncated: a half-printed URL is worse than a small one.
  function fitFontSize(ctx, text, maxWidth, startPx, minPx, stack) {
    var size = startPx;
    while (size > minPx) {
      ctx.font = size + 'px ' + stack;
      if (ctx.measureText(text).width <= maxWidth) break;
      size -= 2;
    }
    ctx.font = size + 'px ' + stack;
    return size;
  }

  /**
   * Compose the printable sign. Returns a canvas, or null if the QR library
   * is missing or the URL is too long to encode.
   */
  function buildSign(opts) {
    if (!window.qrcode) return null;

    var qr;
    try {
      // Error level Q (~25%) rather than M: a door sign gets taped to glass,
      // creased and smudged, and the extra redundancy costs nothing at this
      // physical size for a URL this short.
      qr = window.qrcode(0, 'Q');
      qr.addData(opts.url);
      qr.make();
    } catch (e) {
      return null;
    }

    var canvas = document.createElement('canvas');
    canvas.width = SIGN_W;
    canvas.height = SIGN_H;
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;

    try { ctx.direction = opts.rtl ? 'rtl' : 'ltr'; } catch (e) { /* older engines */ }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, SIGN_W, SIGN_H);

    var maxW = SIGN_W - PAD * 2;
    var mid = SIGN_W / 2;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    var y = PAD + 76;

    // Event title — up to two lines, then ellipsis.
    ctx.fillStyle = '#1c1c22';
    ctx.font = '700 66px ' + FONT_STACK;
    var titleLines = wrapLines(ctx, opts.title, maxW, 2);
    for (var i = 0; i < titleLines.length; i++) {
      ctx.fillText(titleLines[i], mid, y);
      y += 80;
    }

    // Date / time / location, when the caller has one.
    if (opts.meta) {
      ctx.fillStyle = '#80808a';
      ctx.font = '34px ' + FONT_STACK;
      var metaLines = wrapLines(ctx, opts.meta, maxW, 2);
      y += 6;
      for (var j = 0; j < metaLines.length; j++) {
        ctx.fillText(metaLines[j], mid, y);
        y += 44;
      }
    }

    // The instruction. This is the line a passer-by reads first, so it sits
    // directly above the code and outweighs the meta line.
    y += 36;
    ctx.fillStyle = '#7e612f';
    ctx.font = '600 48px ' + FONT_STACK;
    var headlineLines = wrapLines(ctx, opts.headline, maxW, 2);
    for (var k = 0; k < headlineLines.length; k++) {
      ctx.fillText(headlineLines[k], mid, y);
      y += 58;
    }

    // The code itself, squared and centred in whatever vertical room is left
    // above the address line.
    var urlBlockH = 120;
    var available = SIGN_H - PAD - urlBlockH - y;
    var boxSize = Math.min(maxW, Math.max(320, available - 40));
    var modules = qr.getModuleCount();
    var cell = Math.max(1, Math.floor(boxSize / modules));
    var drawn = cell * modules;
    var qx = Math.round(mid - drawn / 2);
    var qy = Math.round(y + (available - drawn) / 2);

    ctx.fillStyle = '#000000';
    for (var r = 0; r < modules; r++) {
      for (var c = 0; c < modules; c++) {
        if (qr.isDark(r, c)) ctx.fillRect(qx + c * cell, qy + r * cell, cell, cell);
      }
    }

    // The address, printed so someone who cannot scan can still type it.
    var urlY = SIGN_H - PAD - 24;
    ctx.fillStyle = '#4f4f57';
    try { ctx.direction = 'ltr'; } catch (e) { /* URLs are always LTR */ }
    ctx.textAlign = 'center';
    fitFontSize(ctx, opts.urlLabel, maxW, 34, 16, MONO_STACK);
    ctx.fillText(opts.urlLabel, mid, urlY);

    return canvas;
  }

  var els = null;
  var state = { blob: null, filename: 'event-qr.png', lastFocus: null };

  function build() {
    if (els) return els;
    injectStyles();
    var back = document.createElement('div');
    back.className = 'eqr-backdrop';
    back.id = 'eqrBackdrop';
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-labelledby', 'eqrTitle');
    back.innerHTML = [
      '<div class="eqr-card">',
      '  <div class="eqr-head eqr-noprint">',
      '    <div style="flex:1;min-width:0;">',
      '      <h3 id="eqrTitle"></h3>',
      '      <p id="eqrSub"></p>',
      '    </div>',
      '    <button type="button" class="eqr-x" id="eqrClose" aria-label="Close">&times;</button>',
      '  </div>',
      '  <div class="eqr-preview" id="eqrPreview"></div>',
      '  <div class="eqr-warn eqr-noprint" id="eqrWarn" style="display:none;">',
      '    <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>',
      '    <span id="eqrWarnText"></span>',
      '  </div>',
      '  <p class="eqr-status eqr-noprint" id="eqrStatus"></p>',
      '  <div class="eqr-actions eqr-noprint">',
      '    <button type="button" class="eqr-btn primary" id="eqrPrint"></button>',
      '    <button type="button" class="eqr-btn" id="eqrDownload"></button>',
      '  </div>',
      '</div>'
    ].join('');
    document.body.appendChild(back);

    els = {
      back: back,
      title: back.querySelector('#eqrTitle'),
      sub: back.querySelector('#eqrSub'),
      close: back.querySelector('#eqrClose'),
      preview: back.querySelector('#eqrPreview'),
      warn: back.querySelector('#eqrWarn'),
      warnText: back.querySelector('#eqrWarnText'),
      status: back.querySelector('#eqrStatus'),
      print: back.querySelector('#eqrPrint'),
      download: back.querySelector('#eqrDownload')
    };

    els.close.addEventListener('click', close);
    back.addEventListener('click', function (e) { if (e.target === back) close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && back.classList.contains('open')) close();
    });
    els.print.addEventListener('click', function () { window.print(); });
    els.download.addEventListener('click', doDownload);
    return els;
  }

  function doDownload() {
    if (!state.blob) {
      els.status.className = 'eqr-status eqr-noprint';
      els.status.textContent = eventT('eqr.still_building', 'Still preparing the image — try again in a moment.');
      return;
    }
    try {
      var objectUrl = URL.createObjectURL(state.blob);
      var a = document.createElement('a');
      a.href = objectUrl;
      a.download = state.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 1000);
      els.status.className = 'eqr-status eqr-noprint';
      els.status.textContent = eventT('eqr.downloaded', 'Image saved.');
    } catch (e) {
      els.status.className = 'eqr-status eqr-noprint err';
      els.status.textContent = eventT('eqr.download_failed', 'Could not save the image.');
    }
  }

  function close() {
    if (!els) return;
    els.back.classList.remove('open');
    document.body.style.overflow = '';
    state.blob = null;
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      try { state.lastFocus.focus(); } catch (e) { /* element may be gone */ }
    }
    state.lastFocus = null;
  }

  function safeName(s) {
    return String(s || 'event').replace(/[^a-z0-9_-]+/gi, '-')
      .replace(/^-+|-+$/g, '').toLowerCase().slice(0, 60) || 'event';
  }

  /**
   * Open the door-sales QR for one event.
   *
   * @param {Object} o
   * @param {string} o.url        The public ticket-sales address the code carries.
   * @param {string} o.title      Event title, printed on the sign.
   * @param {string} [o.meta]     Date / time / location line.
   * @param {boolean} [o.isDraft] Event is not published yet — warn before printing.
   */
  function open(o) {
    o = o || {};
    if (!o.url) return;
    var e = build();
    state.lastFocus = document.activeElement;

    var dir = (document.documentElement.getAttribute('dir') || '').toLowerCase();
    var rtl = dir === 'rtl';

    e.title.textContent = eventT('eqr.modal_title', 'Event QR code');
    e.sub.textContent = eventT('eqr.modal_sub', 'Print this and guests can scan to buy tickets at the door.');
    e.close.setAttribute('aria-label', eventT('eqr.close', 'Close'));
    e.print.innerHTML = '<i class="bi bi-printer" aria-hidden="true"></i>' +
      esc(eventT('eqr.print', 'Print QR code'));
    e.download.innerHTML = '<i class="bi bi-download" aria-hidden="true"></i>' +
      esc(eventT('eqr.download', 'Download image'));
    e.status.className = 'eqr-status eqr-noprint';
    e.status.textContent = '';

    // Strip the scheme for the printed address: "quikrsvp.com/form?id=..." is
    // what a person types, and the shorter string prints larger.
    var urlLabel = String(o.url).replace(/^https?:\/\//i, '').replace(/\/$/, '');

    var canvas = buildSign({
      url: o.url,
      urlLabel: urlLabel,
      title: o.title || eventT('eqr.untitled', 'Your event'),
      meta: o.meta || '',
      headline: eventT('eqr.headline', 'Scan to buy tickets'),
      rtl: rtl
    });

    if (!canvas) {
      e.preview.innerHTML = '<p style="padding:1.2rem;margin:0;color:#b00020;font-size:.88rem;">' +
        esc(eventT('eqr.unavailable', 'QR code unavailable — please reload the page and try again.')) + '</p>';
      e.print.disabled = true;
      e.download.disabled = true;
    } else {
      e.print.disabled = false;
      e.download.disabled = false;
      var img = new Image();
      img.alt = eventT('eqr.alt', 'Printable QR code sign for this event');
      img.src = canvas.toDataURL('image/png');
      e.preview.innerHTML = '';
      e.preview.appendChild(img);
      state.filename = safeName(o.title) + '-event-qr.png';
      state.blob = null;
      canvas.toBlob(function (blob) { state.blob = blob; }, 'image/png');
    }

    // A QR gets PRINTED, so it outlives any correction. An unpublished event
    // is the one case where the code is guaranteed dead on arrival, and the
    // warning sits directly above the button that commits it.
    if (o.isDraft) {
      e.warnText.textContent = eventT('eqr.draft_warn',
        'This event is not published yet, so the code will not work for guests. Publish it before printing.');
      e.warn.style.display = 'flex';
    } else {
      e.warn.style.display = 'none';
    }

    e.back.classList.add('open');
    document.body.style.overflow = 'hidden';
    setTimeout(function () { try { e.close.focus(); } catch (err) { /* noop */ } }, 0);
  }

  window.EventQrModal = { open: open, close: close };
})();
